import React, { useEffect, useRef } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { Box, CircularProgress, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import { useChargingStrategy } from 'src/Libs/installations/hooks/installation-configuration/em360/use-charging-strategy'
import { SettingsBox } from 'src/Elements/Data'
import { BlankView, BlankViewImageVariants, BlankViewImageSizes } from 'src/Components/blank-view'
import { EM360_DEFAULT_CHARGING_STRATEGY } from 'src/Libs/installations/constants'

interface Props {
  installationId?: number
}

interface ChargingStrategyFormValues {
  emsBatteryBeforeCarMode: boolean
  emsWeatherRegulatedChargeEnabled: boolean
}

const ChargingStrategyPanel: React.FC<Props> = ({ installationId }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const {
    query: {
      data: deviceSettings,
      isLoading: isDeviceSettingsLoading,
      isSuccess: isDeviceSettingsSuccess,
      isError: isDeviceSettingsError,
      refetch: refetchDeviceSettings,
    },
    mutation: { mutate: saveSettings, isPending: isSaving },
  } = useChargingStrategy(installationId)

  const methods = useForm<ChargingStrategyFormValues>({
    defaultValues: EM360_DEFAULT_CHARGING_STRATEGY,
  })
  const hasInitializedDefaults = useRef(false)

  useEffect(() => {
    hasInitializedDefaults.current = false
  }, [installationId])

  useEffect(() => {
    if (deviceSettings) {
      methods.setValue('emsBatteryBeforeCarMode', deviceSettings.emsBatteryBeforeCarMode ?? EM360_DEFAULT_CHARGING_STRATEGY.emsBatteryBeforeCarMode)
      methods.setValue(
        'emsWeatherRegulatedChargeEnabled',
        deviceSettings.emsWeatherRegulatedChargeEnabled ?? EM360_DEFAULT_CHARGING_STRATEGY.emsWeatherRegulatedChargeEnabled
      )
    }
  }, [methods, deviceSettings])

  useEffect(() => {
    const hasChargingStrategySettings = deviceSettings?.emsBatteryBeforeCarMode != null && deviceSettings.emsWeatherRegulatedChargeEnabled != null

    if (
      !installationId ||
      !isDeviceSettingsSuccess ||
      isDeviceSettingsError ||
      hasChargingStrategySettings ||
      isSaving ||
      hasInitializedDefaults.current
    ) {
      return
    }

    hasInitializedDefaults.current = true
    saveSettings(
      {
        emsBatteryBeforeCarMode: deviceSettings?.emsBatteryBeforeCarMode ?? EM360_DEFAULT_CHARGING_STRATEGY.emsBatteryBeforeCarMode,
        emsWeatherRegulatedChargeEnabled:
          deviceSettings?.emsWeatherRegulatedChargeEnabled ?? EM360_DEFAULT_CHARGING_STRATEGY.emsWeatherRegulatedChargeEnabled,
      },
      {
        onError: () => enqueueErrorStack(t('installation-configuration.em360.charging-strategy.save.error-transmit')),
      }
    )
  }, [deviceSettings, enqueueErrorStack, installationId, isDeviceSettingsError, isDeviceSettingsSuccess, isSaving, saveSettings, t])

  const handleChange = (changedField: keyof ChargingStrategyFormValues): void => {
    if (isSaving) return
    const values = methods.getValues()
    saveSettings(
      { emsBatteryBeforeCarMode: values.emsBatteryBeforeCarMode, emsWeatherRegulatedChargeEnabled: values.emsWeatherRegulatedChargeEnabled },
      {
        onSuccess: () => enqueueSuccessStack(t('installation-configuration.em360.charging-strategy.save.success-transmitted')),
        onError: () => {
          const revertValues: ChargingStrategyFormValues = {
            emsBatteryBeforeCarMode: deviceSettings?.emsBatteryBeforeCarMode ?? EM360_DEFAULT_CHARGING_STRATEGY.emsBatteryBeforeCarMode,
            emsWeatherRegulatedChargeEnabled:
              deviceSettings?.emsWeatherRegulatedChargeEnabled ?? EM360_DEFAULT_CHARGING_STRATEGY.emsWeatherRegulatedChargeEnabled,
          }
          methods.setValue(changedField, revertValues[changedField])
          enqueueErrorStack(t('installation-configuration.em360.charging-strategy.save.error-transmit'))
        },
      }
    )
  }

  const isLoading = isDeviceSettingsLoading
  const isDisabled = !installationId || isLoading || isDeviceSettingsError || isSaving

  const renderContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <Stack alignItems={'center'} justifyContent={'center'} sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      )
    }
    if (isDeviceSettingsError) {
      return (
        <Box sx={{ py: 4 }}>
          <BlankView
            imageVariant={BlankViewImageVariants.Worried}
            imageSize={BlankViewImageSizes.Small}
            title={'installation-configuration.em360.charging-strategy.load.error'}
            action={{ text: 'common.reload', onClick: refetchDeviceSettings }}
          />
        </Box>
      )
    }
    return (
      <>
        <SettingsBox
          title={t('installation-configuration.em360.charging-strategy.charging-priority')}
          suffix={
            <Box sx={{ width: { xs: '100%', md: 280 } }}>
              <TabButtonGroup
                name={'emsBatteryBeforeCarMode'}
                onChange={() => handleChange('emsBatteryBeforeCarMode')}
                disabled={isDisabled}
                options={[
                  { value: false, label: t('installation-configuration.em360.charging-strategy.charging-priority.wallbox') },
                  { value: true, label: t('installation-configuration.em360.charging-strategy.charging-priority.battery') },
                ]}
              />
            </Box>
          }
        />
        <SettingsBox
          title={t('installation-configuration.em360.charging-strategy.weather-forcast')}
          suffix={
            <Box sx={{ width: { xs: '100%', md: 280 } }}>
              <TabButtonGroup
                name={'emsWeatherRegulatedChargeEnabled'}
                onChange={() => handleChange('emsWeatherRegulatedChargeEnabled')}
                disabled={isDisabled}
                options={[
                  { value: true, label: t('installation-configuration.em360.charging-strategy.enabled') },
                  { value: false, label: t('installation-configuration.em360.charging-strategy.disabled') },
                ]}
              />
            </Box>
          }
        />
      </>
    )
  }

  return (
    <AccordionItem title={t('installation-configuration.em360.charging-strategy.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <FormProvider {...methods}>{renderContent()}</FormProvider>
    </AccordionItem>
  )
}

export default ChargingStrategyPanel
